import { useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Selectbutton } from '@/shared/ui/Selectbutton'
import { SectionHeader } from '@/shared/ui/SectionHeader'
import { TextField } from '@/shared/ui/TextField'
import { KakaoPointMap } from '@/shared/ui/KakaoPointMap'
import { CityIcon } from '@/shared/ui/icons/CityIcon'
import { InfoIcon } from '@/shared/ui/icons/InfoIcon'
import { useCommonCodes } from '@/shared/lib/codes'
import type { OrgNewDraft } from '@/public/features/org/types'
import { INTRO_MAX, PC_ORG_LAST_STEP } from '@/public/features/org/constants/orgNew'
import {
  OrgNewPcShell,
  PcFieldRow,
  PcTextarea,
  PC_PRIMARY_BUTTON_CLASS,
} from '@/public/features/org/ui/orgNewPc/parts'
import {
  addressMetaText,
  useKakaoAddressSearch,
  type AddressCandidate,
} from '@/public/features/problem/composables/useKakaoAddressSearch'

/** PC 첫 페이지 — 모바일 1(유형)·2(기업 정보)·3(기업 소개) 단계를 한 화면에 묶는다(시안 1288:18993). */
export function OrgNewPcOrg({ draft }: { draft: OrgNewDraft }) {
  const navigate = useNavigate()
  const orgTypes = useCommonCodes('org_type')

  // 이 페이지가 묶은 세 단계가 모두 끝났는지 — 판정은 draft 가 갖고 있다(화면이 다시 매기지 않는다).
  const canGoNext = draft.firstIncompleteStep > PC_ORG_LAST_STEP

  return (
    <OrgNewPcShell
      title="기업 등록하기"
      active="org"
      error={draft.error}
      footer={
        <button
          type="button"
          className={`${PC_PRIMARY_BUTTON_CLASS} w-[147px]`}
          disabled={!canGoNext}
          onClick={() => navigate('/orgs/new/4')}
        >
          다음
        </button>
      }
    >
      <div className="flex flex-col items-start gap-[44px]">
        <section className="flex w-full flex-col items-start justify-center gap-6">
          <SectionHeader
            no="01"
            title="어떤 유형의 기업인가요?"
            desc="해당하는 기업 유형을 하나 선택해 주세요."
          />
          <div className="flex w-full flex-wrap content-start items-start gap-x-2 gap-y-2.5">
            {(orgTypes.data ?? []).map((t) => (
              <Selectbutton
                key={t.code}
                size="pc-chip"
                // 160px 고정 — 6개 + 8px 간격 5개가 1000px 폭에 정확히 한 행으로 들어간다(시안 1288:19800).
                className="w-[160px]"
                label={t.label}
                isOn={draft.orgTypeCode === t.code}
                onSelect={() => draft.setOrgType(t.code)}
              />
            ))}
          </div>
        </section>

        <section className="flex w-full flex-col items-start gap-6">
          <SectionHeader
            no="02"
            title="기업 정보를 알려주세요."
            desc="기업 확인에 필요한 기본 정보를 입력해 주세요."
          />
          <div className="flex w-full flex-col gap-6">
            <PcFieldRow>
              <NameField draft={draft} />
              <TextField
                size="large"
                label="사업자등록번호"
                placeholder="000-00-00000"
                inputMode="numeric"
                value={draft.bizRegNo}
                onChange={(e) => draft.setBizRegNo(e.target.value)}
              />
            </PcFieldRow>
            <PcFieldRow>
              <AddressField draft={draft} />
              <TextField
                size="large"
                label="대표전화"
                type="tel"
                inputMode="tel"
                placeholder="02-0000-0000"
                value={draft.mainPhone}
                onChange={(e) => draft.setMainPhone(e.target.value)}
              />
            </PcFieldRow>
            <div className="w-[490px]">
              <TextField
                size="large"
                label="홈페이지"
                type="url"
                autoComplete="url"
                placeholder="http://"
                value={draft.homepage}
                onChange={(e) => draft.setHomepage(e.target.value)}
              />
            </div>
          </div>
        </section>

        <section className="flex w-full flex-col items-start gap-6">
          <SectionHeader
            no="03"
            title="어떤 기업인지 소개해 주세요."
            desc="기업의 주요 활동과 서비스를 간단히 알려주세요."
          />
          <div className="flex w-full flex-col gap-6">
            <LogoUpload draft={draft} />
            <div className="flex w-full flex-col gap-[18px]">
              <PcTextarea
                label="기업 소개"
                required
                placeholder="어떤 활동과 서비스를 제공하는 기업인지 소개해 주세요."
                value={draft.intro}
                onChange={draft.setIntro}
                maxLength={INTRO_MAX}
                count={{ current: draft.intro.length, max: INTRO_MAX }}
              />
              <PcTextarea
                label="기업 미션"
                placeholder="기업 미션을 작성해주세요."
                value={draft.mission}
                onChange={draft.setMission}
                maxLength={INTRO_MAX}
              />
              <PcTextarea
                label="기업 비전"
                placeholder="기업 비전을 작성해주세요."
                value={draft.vision}
                onChange={draft.setVision}
                maxLength={INTRO_MAX}
              />
            </div>
          </div>
        </section>
      </div>
    </OrgNewPcShell>
  )
}

function nameMessage(draft: OrgNewDraft): string | undefined {
  if (draft.nameCheck === 'taken') return '이미 사용 중인 기업(단체)명입니다.'
  if (draft.nameCheck === 'ok') return '사용할 수 있는 기업(단체)명입니다.'
  if (draft.name.trim().length > 0) return '기업(단체)명 중복 확인이 필요합니다.'
  return undefined
}

function NameField({ draft }: { draft: OrgNewDraft }) {
  const message = nameMessage(draft)

  return (
    <TextField
      size="large-button"
      label="기업(단체)명"
      required
      placeholder="예: 해봄 협동조합"
      value={draft.name}
      onChange={(e) => draft.setName(e.target.value)}
      button={{ text: '중복 확인', onClick: draft.checkName }}
      disabled={draft.nameCheck === 'checking'}
      {...(message !== undefined ? { message } : {})}
    />
  )
}

function AddressField({ draft }: { draft: OrgNewDraft }) {
  const search = useKakaoAddressSearch()
  const { address } = draft
  const [query, setQuery] = useState(address.text)
  const hasPickedAddress =
    address.lat !== null && address.lng !== null && address.regionCode !== ''

  const pick = useCallback(
    async (c: AddressCandidate) => {
      const resolved = await search.resolve(c)
      draft.setAddress({
        text: resolved.text,
        detail: address.detail,
        regionCode: resolved.regionCode,
        legalCode: resolved.legalCode,
        regionName: resolved.regionName,
        legalName: resolved.legalName,
        postalCode: resolved.postalCode ?? '',
        lat: resolved.lat,
        lng: resolved.lng,
      })
      setQuery(resolved.text)
      search.clear()
    },
    [address.detail, draft, search],
  )

  const clearPickedAddress = useCallback(() => {
    // 글자를 고치면 앞서 고른 코드를 버린다 — 코드와 글자가 다른 곳을 가리키면 안 된다.
    draft.setAddress({
      text: '',
      detail: address.detail,
      regionCode: '',
      legalCode: '',
      regionName: '',
      legalName: '',
      postalCode: '',
      lat: null,
      lng: null,
    })
  }, [address.detail, draft])

  return (
    <div className="flex flex-col gap-2">
      <TextField
        size="large-button"
        label="소재지 주소"
        required
        placeholder="주소를 입력해 주세요."
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          if (address.regionCode !== '') clearPickedAddress()
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            void search.search(query)
          }
        }}
        button={{ text: '주소 찾기', onClick: () => void search.search(query) }}
        disabled={search.isLoading}
      />

      <TextField
        size="large"
        placeholder="상세 주소를 입력해 주세요."
        value={address.detail}
        onChange={(e) =>
          draft.setAddress({
            text: address.text,
            detail: e.target.value,
            regionCode: address.regionCode,
            legalCode: address.legalCode,
            regionName: address.regionName,
            legalName: address.legalName,
            postalCode: address.postalCode,
            lat: address.lat,
            lng: address.lng,
          })
        }
      />

      {search.isLoading && (
        <p
          className="m-0 flex items-center gap-1 text-label font-medium text-violet-600 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0"
          role="status"
        >
          <InfoIcon />
          <span>주소를 찾는 중입니다.</span>
        </p>
      )}

      {search.error !== null && (
        <p
          className="m-0 flex items-center gap-1 text-label font-medium text-[#f54550] [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0"
          role="alert"
        >
          <InfoIcon />
          <span>{search.error}</span>
        </p>
      )}

      {search.results.length > 0 && (
        <ul
          className="mt-2 mb-0 flex max-h-[232px] list-none flex-col overflow-y-auto rounded-[10px] border border-white-400 p-0 [&>li:first-child>button]:border-t-0"
          aria-label="주소 검색 결과"
        >
          {search.results.map((c) => (
            <li key={c.id ?? `${c.jibun}-${c.text}`}>
              <button
                type="button"
                className="ui-pressable flex w-full cursor-pointer flex-col gap-0.5 border-0 border-t border-t-white-200 bg-none px-3 py-2.5 text-left"
                onClick={() => void pick(c)}
              >
                {c.placeName !== undefined && (
                  <span className="text-body-sm font-medium text-black-600">{c.placeName}</span>
                )}
                <span
                  className={
                    c.placeName === undefined
                      ? 'text-body-sm font-medium text-black-600'
                      : 'text-label text-black-100'
                  }
                >
                  {c.text}
                </span>
                {c.jibun !== c.text && <span className="text-label text-black-100">{c.jibun}</span>}
                {addressMetaText(c.regionName, c.legalName, c.postalCode) !== '' && (
                  <span className="text-label text-black-100">
                    {addressMetaText(c.regionName, c.legalName, c.postalCode)}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {search.hasSearched &&
        !search.isLoading &&
        search.error === null &&
        search.results.length === 0 && (
          <p
            className="m-0 flex items-center gap-1 text-label font-medium text-[#f54550] [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0"
            role="alert"
          >
            <InfoIcon />
            <span>찾는 주소가 없습니다. 동 이름이나 도로명으로 다시 찾아주세요.</span>
          </p>
        )}

      {hasPickedAddress && address.lat !== null && address.lng !== null && (
        <KakaoPointMap lat={address.lat} lng={address.lng} />
      )}

      {address.regionCode !== '' && (
        <>
          <p className="m-0 flex items-center gap-1 text-label font-medium text-violet-600 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0">
            <InfoIcon />
            <span>{address.regionName} 으로 등록됩니다.</span>
          </p>
          <p className="m-0 pl-5 text-label text-black-100">
            {addressMetaText(address.regionName, address.legalName, address.postalCode)}
          </p>
        </>
      )}
    </div>
  )
}

function LogoUpload({ draft }: { draft: OrgNewDraft }) {
  const file = useRef<HTMLInputElement>(null)
  const { logo } = draft

  return (
    <div className="flex flex-col gap-2">
      <span className="text-body-sm font-normal text-black-600">로고/아이콘</span>
      <div className="flex items-center gap-3">
        <div
          className="relative flex h-[60px] w-[60px] shrink-0 items-center justify-center overflow-hidden rounded-[99px] border border-black/5 bg-violet-100"
          style={{ background: draft.logoTone }}
        >
          {logo === null ? (
            <span className="flex items-center justify-center text-black-100 opacity-25" aria-hidden>
              <CityIcon size={36} />
            </span>
          ) : (
            <>
              <img className="block h-full w-full object-cover" src={logo.dataUrl} alt="올린 로고" />
              <button
                type="button"
                className="ui-pressable absolute top-[-1px] left-[-1px] h-6 w-6 cursor-pointer rounded-[99px] border-0 bg-black-500 p-0 text-body-md leading-none text-white-50"
                onClick={() => draft.setLogo(null)}
                aria-label="로고 지우기"
              >
                ×
              </button>
            </>
          )}
        </div>

        <div className="flex flex-col items-start justify-center gap-1.5">
          <button
            type="button"
            className="ui-pressable inline-flex h-10 cursor-pointer items-center justify-center gap-1.5 rounded-xl border border-white-400 bg-white-200 px-3 py-2.5 font-base text-body-md whitespace-nowrap text-black-600"
            onClick={() => file.current?.click()}
          >
            {logo === null ? '이미지 올리기' : '변경하기'}
          </button>
          <p className="m-0 text-body-sm-reading font-medium text-black-100">
            이미지는 2MB이하로 업로드 가능합니다.
          </p>
        </div>

        <input
          ref={file}
          type="file"
          className="hidden"
          accept="image/*"
          onChange={(e) => {
            draft.setLogo(e.target.files?.[0] ?? null)
            // 같은 파일을 다시 고를 수 있게 값을 비운다(브라우저가 같은 값이면 change 를 안 낸다).
            e.target.value = ''
          }}
        />
      </div>
      {draft.logoError !== null && (
        <p
          className="m-0 flex items-center gap-1 text-label font-medium text-[#f54550] [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0"
          role="alert"
        >
          <InfoIcon />
          <span>{draft.logoError}</span>
        </p>
      )}
    </div>
  )
}
